import React from 'react';
import styled from 'styled-components';
import { CurriculumHeaderImage } from '../../../components/CurriculumHeader';
import { createCurriculum } from '../../../hooks/useCurriculum';
import { getRecentlyPaired } from '../../pairing';
import { CurriculumAccess } from '../api/getCurriculumAccess';
import { BrandCode } from '../models/curriculum';
import { CurriculumBrand } from '../models/curriculumBrand';
import { curriculumConfig } from '../models/curriculumConfig';
import { BibleStudyAgeGroups } from './BibleStudyAgeGroups';
import { AgeCategoryEntry, BibleStudyCard, BibleStudyDescription } from './BibleStudyCardsElements';
import { BibleStudyHeaderImg } from './BibleStudyHeaderImg';
import { PreviewBibleStudiesAgeGroups } from './PreviewBibleStudiesAgeGroups';

type BibleStudyCardsProps = {
  curriculumAccess: CurriculumAccess;
  recentlyPaired?: ReturnType<typeof getRecentlyPaired>;
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  grid-gap: 24px;
`;

type PreviewDisplayConfigOptions = {
  visibility: string;
  accentColor: string;
};

const PreviewDisplayConfig: Record<string, PreviewDisplayConfigOptions> = {
  VBS: {
    visibility: 'visibleWithAccess',
    accentColor: '#EF7648',
  },
  HYFICHRISTMAS: {
    visibility: window.featureToggles.get('HYFI_PREVIEW') === true ? 'visible' : 'hidden',
    accentColor: '#099799',
  },
};

const isPreviewBrand = (brandCode: BrandCode) => {
  return !curriculumConfig.standardCurriculumLines.includes(brandCode);
};

const hasVisibilityWithAccess = (
  visibility: PreviewDisplayConfigOptions['visibility'],
  ageEntries: AgeCategoryEntry[]
) => {
  return visibility === 'visibleWithAccess' && ageEntries.some(([_, age]) => age.access === 'Active');
};

const isDisplayable = (
  cardData: [CurriculumBrand | undefined, AgeCategoryEntry[]]
): cardData is [CurriculumBrand, AgeCategoryEntry[]] => {
  const [brand, ageEntries] = cardData;
  if (!brand) return false;
  if (!isPreviewBrand(brand.code)) return true;

  if (!PreviewDisplayConfig[brand.code]) return false;
  if (PreviewDisplayConfig[brand.code].visibility === 'visible') return true;
  if (hasVisibilityWithAccess(PreviewDisplayConfig[brand.code].visibility, ageEntries)) return true;
  return false;
};

export default function BibleStudyCards({ curriculumAccess, recentlyPaired }: BibleStudyCardsProps) {
  const getTheme = (brandCode: BrandCode) => (['ETB', 'YOU'].includes(brandCode) ? 'dark' : 'light');

  return (
    <Grid>
      {Object.entries(curriculumAccess)
        .map(
          ([brandCode, ageCategories]) =>
            [createCurriculum(brandCode as BrandCode)?.brand, Object.entries(ageCategories)] as [
              CurriculumBrand | undefined,
              AgeCategoryEntry[]
            ]
        )
        .filter(isDisplayable)
        .map(([brand, ageEntries]) => (
          <BibleStudyCard key={brand.code} backgroundColor={brand.background} theme={getTheme(brand.code)}>
            {(brand.logo && <BibleStudyHeaderImg>{brand.logo}</BibleStudyHeaderImg>) ||
              (brand.imgSrc && <CurriculumHeaderImage src={brand.imgSrc} alt={`${brand.name}`} />)}
            <BibleStudyDescription>{brand.description}</BibleStudyDescription>
            {isPreviewBrand(brand.code) ? (
              <PreviewBibleStudiesAgeGroups
                brandCode={brand.code}
                brandName={brand.name}
                subscribedAgeGroups={ageEntries.filter(([_, ageData]) => ageData.access === 'Active')}
                unsubscribedAgeGroups={ageEntries.filter(([_, ageData]) => ageData.access !== 'Active')}
                recentlyPaired={recentlyPaired}
                previewConfig={PreviewDisplayConfig[brand.code]}
              />
            ) : (
              <BibleStudyAgeGroups
                brandCode={brand.code}
                brandName={brand.name}
                subscribedAgeGroups={ageEntries.filter(([_, ageData]) => ageData.access === 'Active')}
                unsubscribedAgeGroups={ageEntries.filter(([_, ageData]) => ageData.access !== 'Active')}
                recentlyPaired={recentlyPaired}
              />
            )}
          </BibleStudyCard>
        ))}
    </Grid>
  );
}
